import React, { useState } from 'react';
import type { CreateActivityRequest } from '@troia-v3/types';
import { SALES_ACTIVITY_ACTIONS } from '@troia-v3/types';
import { UsersMultiSelect, Select, Input, Textarea } from '@/shared/components/ui';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { useApiError } from '@/hooks/useApiError';
import { Icon } from '@/shared/icons'
export interface AddActivityFormProps {
  onSubmit: (data: Omit<CreateActivityRequest, 'entityType' | 'entityId' | 'category' | 'actorType' | 'actorId'>) => Promise<void>;
  loading?: boolean;
}

/**
 * Componente de formulário para adicionar atividade ao lead
 *
 * Formulário sempre visível com campos em linha:
 * - Título, Ação e Data na mesma linha
 * - Descrição abaixo ocupando largura total
 * - Seleção de usuários vinculados ao lado do botão
 *
 * Tipo de atividade fixo: 'communication'
 * Ações pré-definidas comuns em CRM
 */
export const AddActivityForm: React.FC<AddActivityFormProps> = ({
  onSubmit,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    title: '',
    action: '',
    description: '',
    occurredAt: '',
    linkedUserIds: [] as string[],
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const { handleApiFieldError } = useApiError();

  // Buscar usuários para o dropdown
  const { data: users = [], isLoading: loadingUsers } = useUsersDropdown({ status: 'active' });

  const updateField = (field: string, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpar erro do campo ao digitar
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validação frontend
    const errors: Record<string, string> = {};
    if (!formData.title.trim()) {
      errors.title = 'Título é obrigatório';
    }
    if (!formData.action.trim()) {
      errors.action = 'Ação é obrigatória';
    }
    if (!formData.description.trim()) {
      errors.description = 'Descrição é obrigatória';
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    // Montar relatedEntities com os usuários selecionados
    const relatedEntities = formData.linkedUserIds.map(userId => {
      const user = users.find(u => u.id === userId);
      return {
        id: userId,
        type: 'user' as const,
        name: user?.name
      };
    });

    // Determinar status automaticamente baseado na data
    let status: 'pending' | 'completed' = 'completed';
    if (formData.occurredAt) {
      const occurredDate = new Date(formData.occurredAt);
      const now = new Date();
      if (occurredDate > now) {
        status = 'pending';
      }
    }

    try {
      await onSubmit({
        type: 'communication',
        action: formData.action.trim(),
        title: formData.title.trim(),
        description: formData.description.trim(),
        occurredAt: formData.occurredAt || undefined,
        status,
        relatedEntities: relatedEntities.length > 0 ? relatedEntities : undefined,
      });

      // Reset form somente no sucesso
      setFormData({
        title: '',
        action: '',
        description: '',
        occurredAt: '',
        linkedUserIds: [],
      });
      setFieldErrors({});
    } catch (error) {
      // Mapear field errors do backend (422)
      handleApiFieldError(error, setFieldErrors);
    }
  };

  // Actions com tracking nas Goals (consultadas em activities por value):
  //   - SALES_ACTIVITY_ACTIONS.CALL_MADE      → métrica callsMade
  //   - SALES_ACTIVITY_ACTIONS.LEAD_QUALIFIED → métrica qualifiedLeads (fallback)
  //   - SALES_ACTIVITY_ACTIONS.PROPOSAL_SENT  → métrica proposalsSent
  // Demais actions são livres (não rastreadas em goals).
  const actionOptions = [
    { value: '', label: 'Selecione uma ação' },
    { value: SALES_ACTIVITY_ACTIONS.CALL_MADE, label: SALES_ACTIVITY_ACTIONS.CALL_MADE },
    { value: SALES_ACTIVITY_ACTIONS.LEAD_QUALIFIED, label: SALES_ACTIVITY_ACTIONS.LEAD_QUALIFIED },
    { value: SALES_ACTIVITY_ACTIONS.PROPOSAL_SENT, label: SALES_ACTIVITY_ACTIONS.PROPOSAL_SENT },
    { value: 'E-mail enviado', label: 'E-mail enviado' },
    { value: 'WhatsApp enviado', label: 'WhatsApp enviado' },
    { value: 'Reunião agendada', label: 'Reunião agendada' },
    { value: 'Reunião realizada', label: 'Reunião realizada' },
    { value: 'Follow-up realizado', label: 'Follow-up realizado' },
    { value: 'Visita agendada', label: 'Visita agendada' },
    { value: 'Visita realizada', label: 'Visita realizada' },
    { value: 'Contato via telefone', label: 'Contato via telefone' },
    { value: 'Contato via e-mail', label: 'Contato via e-mail' },
    { value: 'Negociação em andamento', label: 'Negociação em andamento' },
    { value: 'Documentação enviada', label: 'Documentação enviada' },
    { value: 'Contrato enviado', label: 'Contrato enviado' },
    { value: 'Outras ações', label: 'Outras ações' },
  ];

  return (
    /* Mockup drwDetail "Nova atividade": row2 (Título*|Ação*), Descrição*, botão à direita.
       Data e "Vincular usuários" são campos reais SEM slot no mockup — mantidos em
       row2 na mesma gramática (anotado no audit). */
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[13px] font-semibold mb-1.5" style={{ color: '#334155' }}>
            Título <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <Input
            value={formData.title}
            onChange={(e) => updateField('title', e.target.value)}
            placeholder="Apresentação da proposta"
            maxLength={200}
            error={fieldErrors.title}
          />
          {fieldErrors.title && (
            <p className="mt-1 text-xs" style={{ color: 'var(--theme-error-color)' }}>{fieldErrors.title}</p>
          )}
        </div>
        <Select
          label="Ação"
          required
          value={formData.action}
          onChange={(val) => updateField('action', val)}
          placeholder="Selecione uma ação"
          options={actionOptions.filter(o => o.value !== '').map((option) => ({
            value: option.value,
            label: option.label
          }))}
          error={fieldErrors.action}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[13px] font-semibold mb-1.5" style={{ color: '#334155' }}>
            Data
          </label>
          <Input
            type="datetime-local"
            value={formData.occurredAt}
            onChange={(e) => updateField('occurredAt', e.target.value)}
          />
        </div>
        <div>
          <label className="block text-[13px] font-semibold mb-1.5" style={{ color: '#334155' }}>
            Vincular usuários
          </label>
          <UsersMultiSelect
            users={users}
            selectedUserIds={formData.linkedUserIds}
            onChange={(userIds) => updateField('linkedUserIds', userIds)}
            placeholder="Vincular usuários"
            loading={loadingUsers}
          />
        </div>
      </div>

      <Textarea
        label="Descrição"
        required
        value={formData.description}
        onChange={(e) => updateField('description', e.target.value)}
        placeholder="Detalhe o que foi feito…"
        rows={2}
        resize="none"
        error={fieldErrors.description}
      />

      {/* Botão à direita (`.b.b-prim` 38px) */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center justify-center gap-[7px] h-[38px] px-4 rounded-[9px] text-sm font-semibold text-white transition-colors disabled:opacity-50"
          style={{ backgroundColor: '#8b5cf6' }}
          onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#7c3aed'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#8b5cf6'; }}
          data-testid="lead-activity-submit"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin" />
              Adicionando…
            </>
          ) : (
            <>
              <Icon name="plus" className="w-3.5 h-3.5" />
              Adicionar Atividade
            </>
          )}
        </button>
      </div>
    </form>
  );
};
